// ProfileDropdown — Profile, Dashboard, Logout only
import { LayoutDashboard, LogOut, User as UserIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import type { User } from '../../types'

interface ProfileDropdownProps {
  user: User | null
}

export default function ProfileDropdown({ user }: ProfileDropdownProps) {
  const { logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) setIsOpen(false)
    }
    if (isOpen) document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const item = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', color: 'var(--text-secondary)',
    fontSize: 13, textDecoration: 'none', transition: 'all .15s',
    width: '100%', background: 'transparent', border: 'none',
    textAlign: 'left' as const, cursor: 'pointer',
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border-default)',
          background: 'var(--card-bg)', color: 'var(--text-primary)',
          cursor: 'pointer', transition: 'all .2s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'var(--card-bg)')}
      >
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
          color: '#fff', fontSize: 12, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {(user?.display_name ?? user?.username ?? 'U').charAt(0).toUpperCase()}
        </div>
        <span className="desktop-only" style={{ fontSize: 13, fontWeight: 700 }}>
          {user?.display_name || user?.username || 'User'}
        </span>
      </button>

      {isOpen && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 44,
          background: 'var(--card-bg)', border: '1px solid var(--border-default)',
          borderRadius: 10, minWidth: 200, zIndex: 1000,
          boxShadow: '0 8px 32px rgba(0,0,0,.5)', padding: '6px 0',
        }}>
          {user?.id && (
            <Link
              to={`/profile/${user?.username ?? user?.id}`}
              role="menuitem"
              style={item}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--text-primary)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
            >
              <UserIcon size={16} />
              Profile
            </Link>
          )}

          <Link
            to="/dashboard"
            role="menuitem"
            style={{ ...item, color: '#6366f1', fontWeight: 600 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            <LayoutDashboard size={16} color="#6366f1" />
            Dashboard
          </Link>

          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 4, paddingTop: 4 }}>
            <button
              onClick={() => void logout()}
              role="menuitem"
              style={{ ...item, color: '#f85149' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
